import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from 'src/app/private/services/api.service';
import { environment } from 'src/environments/environment';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import { CustomerIntelligenceResponse } from 'src/app/models/customer-intelligence.model';

@Injectable({
  providedIn: 'root',
})
export class Customer360Service {
  constructor(
    private api: ApiService,
    private http: HttpClient,
    private localStorage: LocalStorageService,
  ) {}

  extractReceiptOCR(imageBase64: string): Observable<any> {
    const url =
      (environment as any).receiptOcrUrl ||
      'https://dhd67xzre4lgoybfagmdvdzbeu0vapyw.lambda-url.us-east-1.on.aws/';
    const apiKey =
      (environment as any).receiptOcrApiKey ||
      'rcpt_live_6e2a3341b02d23211adfc210f483c2e5a0bc895240c32796';

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
    });

    return this.http.post(url, { image_base64: imageBase64 }, { headers });
  }

  getAudienceCustomerIntelligence(clientId?: number | string): Observable<any> {
    const url = (environment as any).customerIntelligenceAudienceUrl;

    const finalClientId = String(
      clientId ?? this.localStorage.getClientId() ?? '4',
    );
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this.http.post(url, { client_id: finalClientId }, { headers });
  }

  private scope(
    leadId: number | string,
    clientId?: number | string,
    centerId?: number | string | null,
  ) {
    return {
      client_id: clientId ?? this.localStorage.getClientId() ?? 4,
      center_id: centerId ?? this.localStorage.getCenterId() ?? 5,
      lead_id: leadId,
    };
  }

  private clientScope(
    clientId?: number | string,
    centerId?: number | string | null,
  ) {
    return {
      client_id: clientId ?? this.localStorage.getClientId() ?? 4,
      center_id: centerId ?? this.localStorage.getCenterId() ?? 5,
    };
  }

  /**
   * Plain JSON capture API (no JWT / no encrypted body).
   * channel (required): same value as lead_source (e.g. Webform, Bot, Landing Page)
   * customer.name (required), plus at least one of phone, email, or external_customer_id
   */
  captureCustomerActivity(payload: {
    client_id?: number | string;
    center_id?: number | string;
    channel: string;
    activity_type?: string;
    customer: {
      name: string;
      phone?: string;
      email?: string;
      external_customer_id?: string;
      country_code?: string;
      metadata?: Record<string, unknown>;
    };
    event?: Record<string, unknown>;
    source_system?: string;
    external_event_id?: string;
  }): Observable<any> {
    return this.api.post(environment.captureCustomerActivity, {
      ...this.clientScope(payload.client_id, payload.center_id),
      ...payload,
    });
  }

  extractCustomerActivityDocument(payload: {
    client_id: number | string;
    center_id?: number | string;
    file_name: string;
    file_base64: string;
    file_type?: string;
  }): Observable<any> {
    return this.api.post(environment.extractCustomerActivityDocument, payload);
  }

  uploadCustomerCaptureDocument(
    file: File,
    leadId: number | string,
    clientId: number | string,
    centerId?: number | string | null,
    channel?: string,
  ): Observable<any> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('client_id', String(clientId));
    formData.append('lead_id', String(leadId));
    if (centerId != null && centerId !== '') {
      formData.append('center_id', String(centerId));
    }
    if (channel) {
      formData.append('channel', channel);
    }
    return this.api.post(environment.uploadCustomerCaptureDocument, formData);
  }

  getCustomerProfile(
    leadId: number | string,
    clientId?: number | string,
  ): Observable<any> {
    return this.api.post(
      environment.getCustomerProfile,
      this.scope(leadId, clientId),
    );
  }

  getCustomerTransactions(
    leadId: number | string,
    page = 0,
    limit = 20,
    clientId?: number | string,
  ): Observable<any> {
    return this.api.post(environment.getCustomerTransactions, {
      ...this.scope(leadId, clientId),
      page,
      limit,
    });
  }

  getCustomerLoyalty(
    leadId: number | string,
    clientId?: number | string,
  ): Observable<any> {
    return this.api.post(
      environment.getCustomerLoyalty,
      this.scope(leadId, clientId),
    );
  }

  getCustomerOffers(
    leadId: number | string,
    clientId?: number | string,
  ): Observable<any> {
    return this.api.post(
      environment.getCustomerOffers,
      this.scope(leadId, clientId),
    );
  }

  getCustomerBehaviorSummary(
    leadId: number | string,
    clientId?: number | string,
  ): Observable<any> {
    return this.api.post(
      environment.getCustomerBehaviorSummary,
      this.scope(leadId, clientId),
    );
  }

  getLeadCustomerAnalytics(leadId: number | string): Observable<any> {
    return this.api.post(
      environment.getLeadCustomerAnalytics,
      this.scope(leadId),
    );
  }

  getCustomerBehaviorAnalytics(): Observable<any> {
    return this.api.post(environment.getCustomerBehaviorAnalytics, {
      client_id: this.localStorage.getClientId(),
    });
  }

  getCustomerFeedback(
    leadId: number | string,
    page = 0,
    limit = 20,
  ): Observable<any> {
    return this.api.post(environment.getCustomerFeedback, {
      ...this.scope(leadId),
      page,
      limit,
    });
  }

  createCustomerFeedback(
    leadId: number | string,
    payload: {
      feedback_text: string;
      rating?: number;
      channel?: string;
      created_by?: number;
    },
  ): Observable<any> {
    return this.api.post(environment.createCustomerFeedback, {
      ...this.scope(leadId),
      ...payload,
      source: 'crm',
    });
  }

  getCustomerDocuments(
    leadId: number | string,
    page = 0,
    limit = 20,
  ): Observable<any> {
    return this.api.post(environment.getCustomerDocuments, {
      ...this.scope(leadId),
      page,
      limit,
    });
  }

  createCustomerDocument(
    leadId: number | string,
    payload: {
      document_name: string;
      file_url?: string;
      file_base64?: string;
      file_type?: string;
      file_size?: number;
      uploaded_by?: number;
    },
  ): Observable<any> {
    return this.api.post(environment.createCustomerDocument, {
      ...this.scope(leadId),
      ...payload,
    });
  }

  /** Plain JSON document APIs with explicit client/center (simulation & capture pages). */
  getCustomerDocumentsForScope(
    leadId: number | string,
    clientId: number | string,
    centerId?: number | string | null,
    page = 0,
    limit = 20,
  ): Observable<any> {
    return this.api.post(environment.getCustomerDocuments, {
      client_id: clientId,
      center_id: centerId ?? undefined,
      lead_id: leadId,
      page,
      limit,
    });
  }

  createCustomerDocumentForScope(
    leadId: number | string,
    clientId: number | string,
    centerId: number | string | null | undefined,
    payload: {
      document_name: string;
      file_url?: string;
      file_base64?: string;
      file_type?: string;
      file_size?: number;
      uploaded_by?: number;
    },
  ): Observable<any> {
    return this.api.post(environment.createCustomerDocument, {
      client_id: clientId,
      center_id: centerId ?? undefined,
      lead_id: leadId,
      ...payload,
    });
  }

  /** GET AI ML Customer Intelligence Lambda — client_id as 1st param, lead_id as 2nd param */
  getCustomerIntelligence(
    leadId: number | string,
    clientId?: number | string,
  ): Observable<CustomerIntelligenceResponse> {
    const cid = clientId ?? this.localStorage.getClientId();
    const baseUrl =
      (environment as any).customerIntelligenceUrl ||
      'https://nqphwqqlmjkgas4vs2ocdrz67u0qjqeb.lambda-url.us-east-1.on.aws/customer-intelligence/lead/';
    const normalizedBaseUrl = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
    const url = `${normalizedBaseUrl}${cid}/${leadId}`;
    return this.api.get<CustomerIntelligenceResponse>(url);
  }
}
